import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { CEDULAS_F210_INFO, obtenerInfoCedulaF210 } from '../utils/cedulasFiscales'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import marcaAguaLogo from '../assets/logo-marca-agua.png'
import { calcularImpuestoRenta } from '../utils/calculoRenta210'

const AZUL = '#16305c'
const AZUL_CLARO = '#2451a3'
const DORADO = '#c9a227'
const DORADO_CLARO = '#e6c667'
const EDGE_FN_URL = 'https://lrwjahmdpflelhargtet.supabase.co/functions/v1/explicar-declaracion'


const PREGUNTAS = [
  {
    id: 'numero_documento',
    seccion: 'Datos básicos',
    pregunta: '¿Cuál es tu número de cédula o NIT?',
    ayuda: 'Es el número con el que apareces registrado ante la DIAN en tu RUT (Registro Único Tributario).',
    tipo: 'texto',
  },
  {
    id: 'actividad_economica',
    seccion: 'Datos básicos',
    pregunta: '¿Cuál es tu actividad económica principal?',
    ayuda: 'Es el código y nombre de actividad que aparece en tu RUT ante la DIAN. Ejemplo: "0010 - Asalariados". Si no la conoces, revisa tu RUT.',
    tipo: 'texto',
  },
  {
    id: 'tiene_dependientes',
    seccion: 'Datos básicos',
    pregunta: '¿Tienes dependientes económicos a tu cargo?',
    ayuda: 'Son personas que dependen económicamente de ti: hijos hasta 18 años (o hasta 23 años si estudian), hijos en situación de dependencia física, tu cónyuge sin ingresos propios, o tus padres y hermanos en situación de dependencia económica. Tenerlos te da derecho a una deducción en tu declaración.',
    tipo: 'booleano',
  },
  {
    id: 'num_dependientes',
    seccion: 'Datos básicos',
    pregunta: '¿Cuántos dependientes económicos tienes?',
    ayuda: 'La ley permite deducir dependientes económicos dentro de los topes que fija la DIAN cada año.',
    tipo: 'numero',
    dependeDe: { id: 'tiene_dependientes', valor: true },
  },
  {
    id: 'patrimonio_bruto',
    seccion: 'Patrimonio',
    pregunta: '¿Cuál es tu patrimonio bruto?',
    ayuda: 'Es la suma de TODO lo que posees a 31 de diciembre: casas, vehículos, cuentas bancarias, inversiones, acciones, etc., SIN restar tus deudas. Se declara por el costo fiscal, no necesariamente por el valor comercial.',
    tipo: 'moneda',
    campoDB: 'patrimonio_bruto',
  },
  {
    id: 'deudas',
    seccion: 'Patrimonio',
    pregunta: '¿Cuál es el valor total de tus deudas o pasivos?',
    ayuda: 'Incluye créditos hipotecarios, de vehículo, tarjetas de crédito y préstamos personales que tuvieras vigentes a 31 de diciembre.',
    tipo: 'moneda',
    campoDB: 'deudas',
  },
  {
    id: 'cedulas_seleccionadas',
    seccion: 'Cédulas',
    pregunta: '¿En cuáles de estas categorías recibiste ingresos durante el año?',
    ayuda: 'La DIAN organiza tus ingresos en "cédulas" según su origen. Marca todas las que apliquen; esto define qué preguntas verás a continuación. Por ahora la cédula de Rentas de Trabajo está disponible completa; las demás estarán disponibles pronto.',
    tipo: 'multiselect_cedulas',
    campoDB: 'cedulas_seleccionadas',
  },
  {
    id: 'ingresos_salarios',
    seccion: 'Rentas de Trabajo',
    pregunta: '¿Cuál fue el total de tus ingresos por salarios durante el año?',
    ayuda: 'Suma todo lo recibido por salario, horas extra, bonificaciones, primas, cesantías y comisiones derivadas de una relación laboral. En la cédula de trabajo solo se declaran ingresos: no puedes restar costos ni gastos.',
    tipo: 'moneda',
    cedulas: ['trabajo'],
  },
  {
    id: 'retenciones_en_la_fuente',
    seccion: 'Rentas de Trabajo',
    pregunta: '¿Cuánto te retuvieron en la fuente durante el año?',
    ayuda: 'Es el valor que tu empleador ya le retuvo y pagó a la DIAN a cuenta de tu impuesto. Aparece mes a mes en tu certificado de ingresos y retenciones.',
    tipo: 'moneda',
    cedulas: ['trabajo'],
  },
  {
    id: 'aportes_voluntarios_pension',
    seccion: 'Rentas de Trabajo',
    pregunta: '¿Hiciste aportes voluntarios a fondos de pensión?',
    ayuda: 'Los aportes voluntarios a pensión pueden ser ingreso no constitutivo de renta o renta exenta, según el caso, y reducen tu base gravable. Si no hiciste, deja 0.',
    tipo: 'moneda',
    cedulas: ['trabajo'],
  },
  {
    id: 'aportes_afc',
    seccion: 'Rentas de Trabajo',
    pregunta: '¿Hiciste aportes a cuentas AFC (Ahorro para el Fomento a la Construcción)?',
    ayuda: 'Las cuentas AFC permiten deducir el ahorro destinado a la compra de vivienda, dentro de los límites que fija la ley. Si no aplica, deja 0.',
    tipo: 'moneda',
    cedulas: ['trabajo'],
  },
  {
    id: 'intereses_vivienda',
    seccion: 'Rentas de Trabajo',
    pregunta: '¿Pagaste intereses por crédito de vivienda?',
    ayuda: 'Los intereses pagados en créditos hipotecarios para adquisición de vivienda son deducibles hasta el tope anual que fija la DIAN. Si no aplica, deja 0.',
    tipo: 'moneda',
    cedulas: ['trabajo'],
  },
  {
    id: 'medicina_prepagada',
    seccion: 'Rentas de Trabajo',
    pregunta: '¿Pagaste medicina prepagada?',
    ayuda: 'Los pagos por medicina prepagada tuya, de tu cónyuge y tus dependientes son deducibles hasta el tope anual que fija la DIAN. Si no aplica, deja 0.',
    tipo: 'moneda',
    cedulas: ['trabajo'],
  },
    // ── Honorarios ──────────────────────────────────────────────
  {
    id: 'honorarios_empleados',
    seccion: 'Honorarios',
    pregunta: '¿Contrataste o vinculaste a 2 o más personas para tu actividad durante al menos 90 días del año?',
    ayuda: 'Esto es clave: si SÍ vinculaste 2 o más trabajadores/contratistas asociados a tu actividad por 90 días o más, la DIAN te clasifica en la cédula de Rentas No Laborales, no en Honorarios. Si tu respuesta es "Sí", más adelante también debes marcar "Rentas No Laborales" en la selección de cédulas y registrar ahí estos ingresos en lugar de aquí.',
    tipo: 'booleano',
    cedulas: ['honorarios'],
  },
  {
    id: 'ingresos_honorarios',
    seccion: 'Honorarios',
    pregunta: '¿Cuál fue el total de tus ingresos brutos por honorarios o compensación de servicios personales?',
    ayuda: 'Es lo que recibiste por prestar servicios de forma independiente (sin ser empleado con contrato laboral), antes de restar cualquier costo, gasto o retención.',
    tipo: 'moneda',
    cedulas: ['honorarios'],
  },
  {
    id: 'retenciones_honorarios',
    seccion: 'Honorarios',
    pregunta: '¿Cuánto te retuvieron en la fuente por honorarios durante el año?',
    ayuda: 'Cada vez que te pagaron honorarios, quien te contrató probablemente te retuvo un porcentaje y lo declaró a la DIAN a tu nombre. Súmalo de todos tus certificados de retención del año.',
    tipo: 'moneda',
    cedulas: ['honorarios'],
  },
  {
    id: 'costos_gastos_honorarios',
    seccion: 'Honorarios',
    pregunta: '¿Cuánto gastaste en costos y gastos relacionados directamente con esa actividad?',
    ayuda: 'A diferencia de un salario, en honorarios SÍ puedes restar los gastos necesarios para generar ese ingreso: arriendo de tu oficina o consultorio, servicios, papelería, transporte del trabajo, herramientas, etc. Si no llevas ese detalle, puedes dejarlo en 0 y perderías esa deducción.',
    tipo: 'moneda',
    cedulas: ['honorarios'],
  },
  {
    id: 'dependientes_deduccion_honorarios',
    seccion: 'Honorarios',
    pregunta: '¿Pagaste medicina prepagada durante el año?',
    ayuda: 'Los pagos por medicina prepagada tuya, de tu cónyuge y tus dependientes son deducibles hasta el tope anual que fija la DIAN. Si no aplica, deja 0.',
    tipo: 'moneda',
    cedulas: ['honorarios'],
  },

  // ── Rentas de Capital ───────────────────────────────────────
  {
    id: 'ingresos_arrendamientos',
    seccion: 'Rentas de Capital',
    pregunta: '¿Cuánto recibiste durante el año por arrendamientos de bienes (casas, locales, etc.)?',
    ayuda: 'Suma todo lo que te pagaron por arrendar inmuebles u otros bienes de tu propiedad durante el año.',
    tipo: 'moneda',
    cedulas: ['capital'],
  },
  {
    id: 'ingresos_intereses_financieros',
    seccion: 'Rentas de Capital',
    pregunta: '¿Cuánto recibiste por intereses, rendimientos financieros o regalías?',
    ayuda: 'Incluye intereses de CDTs, cuentas de ahorro que superen los rendimientos exentos, rendimientos de inversiones, o regalías por propiedad intelectual.',
    tipo: 'moneda',
    cedulas: ['capital'],
  },
  {
    id: 'retenciones_capital',
    seccion: 'Rentas de Capital',
    pregunta: '¿Cuánto te retuvieron en la fuente sobre estos ingresos de capital?',
    ayuda: 'Revisa los certificados de retención de tus arrendatarios o de la entidad financiera que te pagó los rendimientos.',
    tipo: 'moneda',
    cedulas: ['capital'],
  },
  {
    id: 'costos_gastos_capital',
    seccion: 'Rentas de Capital',
    pregunta: '¿Tuviste costos o gastos asociados a generar estos ingresos (ej. mantenimiento del inmueble arrendado)?',
    ayuda: 'Ejemplo: administración, mantenimiento, seguros o impuesto predial del inmueble que arrendaste. Si no aplica, deja 0.',
    tipo: 'moneda',
    cedulas: ['capital'],
  },

  // ── Rentas No Laborales ─────────────────────────────────────
  {
    id: 'ingresos_negocio_propio',
    seccion: 'Rentas No Laborales',
    pregunta: '¿Cuál fue el ingreso bruto total de tu negocio propio o actividad comercial durante el año?',
    ayuda: 'Aplica si tienes un negocio, vendes mercancías, prestas servicios habiendo contratado 2 o más personas, o realizaste otras actividades comerciales no cubiertas en las otras cédulas.',
    tipo: 'moneda',
    cedulas: ['no_laboral'],
  },
  {
    id: 'retenciones_no_laboral',
    seccion: 'Rentas No Laborales',
    pregunta: '¿Cuánto te retuvieron en la fuente sobre estos ingresos?',
    ayuda: 'Súmalo de los certificados de retención que te dieron tus clientes o pagadores durante el año.',
    tipo: 'moneda',
    cedulas: ['no_laboral'],
  },
  {
    id: 'costos_gastos_no_laboral',
    seccion: 'Rentas No Laborales',
    pregunta: '¿Cuánto gastaste en costos y gastos de tu negocio (mercancía, empleados, arriendo del local, servicios, etc.)?',
    ayuda: 'En esta cédula puedes restar todos los costos y gastos necesarios para operar tu negocio: mercancía vendida, salarios de empleados, arriendo, servicios públicos, seguros, entre otros.',
    tipo: 'moneda',
    cedulas: ['no_laboral'],
  },

  // ── Pensiones ───────────────────────────────────────────────
  {
    id: 'ingresos_pension',
    seccion: 'Pensiones',
    pregunta: '¿Cuál fue el total de tus mesadas pensionales recibidas durante el año?',
    ayuda: 'Incluye pensión de jubilación, invalidez, vejez, sobrevivientes o riesgos profesionales, sumando lo recibido en todos los meses del año.',
    tipo: 'moneda',
    cedulas: ['pensiones'],
  },
  {
    id: 'retenciones_pension',
    seccion: 'Pensiones',
    pregunta: '¿Cuánto te retuvieron en la fuente sobre tu pensión durante el año?',
    ayuda: 'Aparece en el certificado de ingresos y retenciones que te entrega el fondo de pensiones o Colpensiones.',
    tipo: 'moneda',
    cedulas: ['pensiones'],
  },
]

function esVacio(v) {
  if (v === undefined || v === null) return true
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === 'string') return v.trim() === ''
  return false
}

function obtenerValorRespuesta(declaracion, id) {
  if (!declaracion) return undefined
  const pregunta = PREGUNTAS.find(p => p.id === id)
  if (!pregunta) return undefined
  if (pregunta.campoDB) return declaracion[pregunta.campoDB]
  return declaracion.respuestas ? declaracion.respuestas[id] : undefined
}

function construirPreguntas(declaracion) {
  const seleccionadas = declaracion?.cedulas_seleccionadas || []
  return PREGUNTAS.filter(p => {
    if (p.cedulas && p.cedulas.length > 0) {
      if (!p.cedulas.some(c => seleccionadas.includes(c))) return false
    }
    if (p.dependeDe) {
      const valorDep = obtenerValorRespuesta(declaracion, p.dependeDe.id)
      if (valorDep !== p.dependeDe.valor) return false
    }
    return true
  })
}

function calcularPasoInicial(declaracion) {
  const lista = construirPreguntas(declaracion)
  const idx = lista.findIndex(p => esVacio(obtenerValorRespuesta(declaracion, p.id)))
  return idx === -1 ? lista.length : idx
}

function formatoMoneda(valor) {
  return '$' + Number(valor || 0).toLocaleString('es-CO')
}

// Formatea solo dígitos como miles con punto: "5000" -> "5.000"
function formatearMiles(valorDigitos) {
  const limpio = String(valorDigitos ?? '').replace(/\D/g, '')
  if (!limpio) return ''
  return Number(limpio).toLocaleString('es-CO')
}

function getPrimerDiaAnioActual() {
  return new Date().getFullYear()
}

const inputStyle = {
  padding: '12px 14px',
  borderRadius: 10,
  border: `1.5px solid ${AZUL_CLARO}33`,
  fontSize: 16,
  outline: 'none',
  background: '#fff',
  color: AZUL,
  width: '100%',
  boxSizing: 'border-box',
}

export default function DeclaracionRenta() {
  const [currentUser, setCurrentUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loadingProfile, setLoadingProfile] = useState(true)

  const [vista, setVista] = useState('lista')
  const [declaraciones, setDeclaraciones] = useState([])
  const [loadingDeclaraciones, setLoadingDeclaraciones] = useState(true)

  const [mostrarNuevoAnio, setMostrarNuevoAnio] = useState(false)
  const [anioNuevo, setAnioNuevo] = useState(String(getPrimerDiaAnioActual() - 1))
  const [creandoDeclaracion, setCreandoDeclaracion] = useState(false)

  const [declaracionActual, setDeclaracionActual] = useState(null)
  const [pasoActual, setPasoActual] = useState(0)
  const [valorInput, setValorInput] = useState('')
  const [mostrarAyuda, setMostrarAyuda] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  // ── Estado del asistente de IA (una sola duda por pregunta) ──────────────
  const [iaAbierta, setIaAbierta] = useState(false)
  const [iaDuda, setIaDuda] = useState('')
  const [iaRespuesta, setIaRespuesta] = useState(null)
  const [iaCargando, setIaCargando] = useState(false)
  const [iaError, setIaError] = useState(null)

  const [mostrarConfirmFinalizar, setMostrarConfirmFinalizar] = useState(false)
  const [confirmEliminar, setConfirmEliminar] = useState(null)
  const [generandoPDF, setGenerandoPDF] = useState(false)
  const [declaracionPDF, setDeclaracionPDF] = useState(null)
  const pdfRef = useRef(null)

  const preguntasActuales = declaracionActual ? construirPreguntas(declaracionActual) : []
  const preguntaActual = preguntasActuales[pasoActual]
  const [resultadoImpuesto, setResultadoImpuesto] = useState(null)
  const [explicacionIA, setExplicacionIA] = useState(null)
  const [cargandoExplicacion, setCargandoExplicacion] = useState(false)

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setLoadingProfile(false); setLoadingDeclaraciones(false); return }
      setCurrentUser(user)

      const { data: perfilData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      setProfile(perfilData || null)
      setLoadingProfile(false)

      const { data: declData, error: errDecl } = await supabase
        .from('declaraciones_renta')
        .select('*')
        .eq('user_id', user.id)
        .order('anio_gravable', { ascending: false })
        .order('created_at', { ascending: false })

      if (!errDecl) setDeclaraciones(declData || [])
      setLoadingDeclaraciones(false)
    })()
  }, [])

  useEffect(() => {
    if (!preguntaActual || !declaracionActual) return
    const valorExistente = obtenerValorRespuesta(declaracionActual, preguntaActual.id)
    if (preguntaActual.tipo === 'multiselect_cedulas') {
      setValorInput(Array.isArray(valorExistente) ? valorExistente : [])
    } else if (preguntaActual.tipo === 'booleano') {
      setValorInput(typeof valorExistente === 'boolean' ? valorExistente : null)
    } else if (preguntaActual.tipo === 'moneda') {
      setValorInput(esVacio(valorExistente) ? '' : String(valorExistente))
    } else {
      setValorInput(esVacio(valorExistente) ? '' : valorExistente)
    }
    setMostrarAyuda(false)
    setError(null)
    // Reset del asistente de IA al cambiar de pregunta
    setIaAbierta(false)
    setIaDuda('')
    setIaRespuesta(null)
    setIaError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasoActual, declaracionActual?.id])

  // Si las respuestas cambian (nueva pregunta guardada), invalida el cálculo
  // anterior para forzar que el usuario lo recalcule con los datos actuales.
  useEffect(() => {
    setResultadoImpuesto(null)
    setExplicacionIA(null)
  }, [declaracionActual])

  const nombreCompleto =
    profile?.nombre || profile?.full_name || currentUser?.email || 'Usuario'

  async function crearNuevaDeclaracion() {
    if (!anioNuevo) return
    setCreandoDeclaracion(true)
    setError(null)
    try {
      const { data, error: errInsert } = await supabase
        .from('declaraciones_renta')
        .insert({
          user_id: currentUser.id,
          anio_gravable: Number(anioNuevo),
          estado: 'borrador',
          cedulas_seleccionadas: [],
          respuestas: {},
          patrimonio_bruto: 0,
          deudas: 0,
        })
        .select()
        .single()
      if (errInsert) throw errInsert
      setDeclaraciones(prev => [data, ...prev])
      abrirDeclaracion(data)
      setMostrarNuevoAnio(false)
    } catch (err) {
      console.error(err)
      setError('No se pudo crear la declaración. Intenta de nuevo.')
    } finally {
      setCreandoDeclaracion(false)
    }
  }

  function abrirDeclaracion(decl) {
    setDeclaracionActual(decl)
    setPasoActual(calcularPasoInicial(decl))
    setVista('wizard')
    setError(null)
  }

  async function eliminarDeclaracion(id) {
    try {
      const { error: errDel } = await supabase
        .from('declaraciones_renta')
        .delete()
        .eq('id', id)
      if (errDel) throw errDel
      setDeclaraciones(prev => prev.filter(d => d.id !== id))
    } catch (err) {
      console.error(err)
      setError('No se pudo eliminar la declaración.')
    } finally {
      setConfirmEliminar(null)
    }
  }

  async function guardarPasoActual() {
    if (!preguntaActual) return

    if (preguntaActual.tipo === 'multiselect_cedulas' && valorInput.length === 0) {
      setError('Selecciona al menos una opción.')
      return
    }
    if (preguntaActual.tipo === 'booleano' && valorInput === null) {
      setError('Selecciona una opción.')
      return
    }
    if ((preguntaActual.tipo === 'moneda' || preguntaActual.tipo === 'numero') && valorInput === '') {
      setError('Ingresa un valor (puedes poner 0 si no aplica).')
      return
    }
    if (preguntaActual.tipo === 'texto' && !String(valorInput).trim()) {
      setError('Este campo no puede quedar vacío.')
      return
    }

    setError(null)
    setGuardando(true)
    try {
      let valorFinal = valorInput
      if (preguntaActual.tipo === 'moneda' || preguntaActual.tipo === 'numero') {
        valorFinal = Number(valorInput) || 0
      }

      const patch = {}
      if (preguntaActual.campoDB) {
        patch[preguntaActual.campoDB] = valorFinal
      } else {
        patch.respuestas = { ...(declaracionActual.respuestas || {}), [preguntaActual.id]: valorFinal }
      }

      const { data, error: errUpdate } = await supabase
        .from('declaraciones_renta')
        .update(patch)
        .eq('id', declaracionActual.id)
        .select()
        .single()
      if (errUpdate) throw errUpdate

      setDeclaraciones(prev => prev.map(d => (d.id === data.id ? data : d)))
      setDeclaracionActual(data)

      const nuevaLista = construirPreguntas(data)
      setPasoActual(pasoActual + 1 >= nuevaLista.length ? nuevaLista.length : pasoActual + 1)
    } catch (err) {
      console.error(err)
      setError('No se pudo guardar la respuesta. Intenta de nuevo.')
    } finally {
      setGuardando(false)
    }
  }
    // ── Cambiar automáticamente de Honorarios a No Laboral ───────────────────
  async function moverAHonorariosNoLaboral() {
    setGuardando(true)
    setError(null)
    try {
      const cedulasActuales = declaracionActual.cedulas_seleccionadas || []
      const nuevasCedulas = Array.from(
        new Set(cedulasActuales.filter(c => c !== 'honorarios').concat('no_laboral'))
      )

      const { data, error: errUpdate } = await supabase
        .from('declaraciones_renta')
        .update({
          cedulas_seleccionadas: nuevasCedulas,
          respuestas: { ...(declaracionActual.respuestas || {}), honorarios_empleados: true },
        })
        .eq('id', declaracionActual.id)
        .select()
        .single()
      if (errUpdate) throw errUpdate

      setDeclaraciones(prev => prev.map(d => (d.id === data.id ? data : d)))
      setDeclaracionActual(data)

      // Recalcula en qué paso quedar ahora que cambió el set de preguntas
      const nuevaLista = construirPreguntas(data)
      const idxSiguiente = nuevaLista.findIndex(p => esVacio(obtenerValorRespuesta(data, p.id)))
      setPasoActual(idxSiguiente === -1 ? nuevaLista.length : idxSiguiente)
    } catch (err) {
      console.error(err)
      setError('No se pudo actualizar la cédula. Intenta de nuevo.')
    } finally {
      setGuardando(false)
    }
  }

  function retroceder() {
    if (pasoActual === 0) { setVista('lista'); return }
    setPasoActual(p => p - 1)
  }

  function manejarEnter(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      guardarPasoActual()
    }
  }

  // ── Preguntar a la IA (una sola duda por pregunta) ────────────────────────
  async function preguntarIA() {
    if (!iaDuda.trim() || !preguntaActual) return
    setIaCargando(true)
    setIaError(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const response = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          pregunta: preguntaActual.pregunta,
          ayuda: preguntaActual.ayuda,
          duda: iaDuda.trim(),
        }),
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.error || `Error ${response.status}`)
      }

      const { respuesta } = await response.json()
      setIaRespuesta(respuesta)
    } catch (err) {
      console.error('Error IA:', err)
      setIaError('No se pudo obtener la respuesta. Intenta de nuevo.')
    } finally {
      setIaCargando(false)
    }
  }
    function calcularResultado() {
    const resultado = calcularImpuestoRenta(declaracionActual)
    setResultadoImpuesto(resultado)
    setExplicacionIA(null)
  }

  async function explicarResultadoConIA() {
    if (!resultadoImpuesto) return
    setCargandoExplicacion(true)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const response = await fetch(
        'https://lrwjahmdpflelhargtet.supabase.co/functions/v1/explicar-resultado-renta',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ resultado: resultadoImpuesto }),
        }
      )
      if (!response.ok) throw new Error('Error al explicar')
      const { explicacion } = await response.json()
      setExplicacionIA(explicacion)
    } catch (err) {
      console.error(err)
      setExplicacionIA('No se pudo generar la explicación en este momento.')
    } finally {
      setCargandoExplicacion(false)
    }
  }

  function nuevaDudaIA() {
    setIaRespuesta(null)
    setIaDuda('')
    setIaError(null)
  }

  async function finalizarYDescargar() {
    setGenerandoPDF(true)
    setError(null)
    try {
      const { data, error: errUpdate } = await supabase
        .from('declaraciones_renta')
        .update({ estado: 'finalizada' })
        .eq('id', declaracionActual.id)
        .select()
        .single()
      if (errUpdate) throw errUpdate
      setDeclaracionActual(data)
      setDeclaraciones(prev => prev.map(d => (d.id === data.id ? data : d)))
      await generarYDescargarPDF(data)
    } catch (err) {
      console.error(err)
      setError('No se pudo finalizar la declaración.')
    } finally {
      setGenerandoPDF(false)
      setMostrarConfirmFinalizar(false)
    }
  }

  async function generarYDescargarPDF(decl) {
    setDeclaracionPDF(decl)
    setGenerandoPDF(true)
    setError(null)
    await new Promise(resolve => setTimeout(resolve, 100))
    try {
      const nodo = pdfRef.current
      if (!nodo) throw new Error('No se encontró el contenido a exportar')
      const canvas = await html2canvas(nodo, { scale: 2, backgroundColor: '#ffffff', useCORS: true })
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = (canvas.height * pageWidth) / canvas.width
      pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight)
      const nombreArchivo = `declaracion-renta-${decl.anio_gravable}-${decl.estado}.pdf`

      if (Capacitor.isNativePlatform()) {
        const pdfBase64 = pdf.output('datauristring').split(',')[1]
        const resultado = await Filesystem.writeFile({
          path: nombreArchivo,
          data: pdfBase64,
          directory: Directory.Cache,
        })
        await Share.share({
          title: 'Declaración de Renta',
          text: 'Formulario 210 — guía de apoyo',
          url: resultado.uri,
          dialogTitle: 'Descargar declaración',
        })
      } else {
        pdf.save(nombreArchivo)
      }
    } catch (err) {
      console.error('Error generando PDF:', err)
      setError('No se pudo generar el PDF.')
    } finally {
      setGenerandoPDF(false)
    }
  }

  if (loadingProfile || loadingDeclaraciones) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexDirection: 'column', gap: 20,
        background: '#f4f6fb',
      }}>
        <div style={{
          width: 70, height: 70, borderRadius: 20,
          background: `linear-gradient(135deg, ${AZUL}, ${AZUL_CLARO})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 32, animation: 'pulse 1.5s infinite',
        }}>
          🧾
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: AZUL, marginBottom: 6 }}>
            <span className="app-title">Calypso</span>
          </div>
          <div style={{ fontSize: 14, color: '#64748b' }}>Cargando...</div>
        </div>
      </div>
    )
  }

  if (vista === 'lista') {
    return (
      <div style={{ minHeight: '100vh', background: '#f4f6fb' }}>
        <header style={{
          background: AZUL, padding: '18px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#fff' }}>
              🧾 Declaración de Renta
            </h2>
            <span style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
              padding: '4px 10px', borderRadius: 999,
              background: `linear-gradient(135deg, ${DORADO}, ${DORADO_CLARO})`,
              color: AZUL, textTransform: 'uppercase',
            }}>
              BETA
            </span>
          </div>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)',
              background: 'transparent', fontSize: 12, color: '#fff', cursor: 'pointer',
            }}
          >
            Volver
          </button>
        </header>

        <main style={{ maxWidth: 640, margin: '0 auto', padding: 20 }}>
          <div style={{
            background: '#fff8e1', border: `1px solid ${DORADO}55`,
            borderRadius: 12, padding: '12px 16px', marginBottom: 20,
            fontSize: 13, color: '#8a6d1a', fontWeight: 600,
          }}>
            ⚠️ Esta función corresponde al <strong>Formulario 210 de la DIAN</strong> y solo está disponible para declarantes en <strong>Colombia</strong>.
          </div>

          {error && (
            <div style={{
              fontSize: 13, color: '#b91c1c', background: '#fef2f2',
              border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          <button
            onClick={() => setMostrarNuevoAnio(true)}
            style={{
              width: '100%', padding: '14px', borderRadius: 12, border: 'none',
              background: `linear-gradient(135deg, ${AZUL}, ${AZUL_CLARO})`,
              color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
              marginBottom: 24, boxShadow: '0 4px 14px rgba(22,48,92,0.3)',
            }}
          >
            + Nueva declaración de renta
          </button>

          {mostrarNuevoAnio && (
            <div style={{
              background: '#fff', border: `1px solid ${AZUL}22`, borderRadius: 12,
              padding: 16, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: AZUL }}>
                ¿Para qué año gravable es esta declaración?
              </label>
              <input
                type="number"
                value={anioNuevo}
                onChange={(e) => setAnioNuevo(e.target.value)}
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={crearNuevaDeclaracion}
                  disabled={creandoDeclaracion}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 10, border: 'none',
                    background: AZUL, color: '#fff', fontWeight: 700, cursor: 'pointer',
                    opacity: creandoDeclaracion ? 0.7 : 1,
                  }}
                >
                  {creandoDeclaracion ? 'Creando...' : 'Empezar'}
                </button>
                <button
                  onClick={() => setMostrarNuevoAnio(false)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${AZUL}33`,
                    background: 'transparent', color: AZUL, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <h3 style={{ fontSize: 14, color: '#64748b', marginBottom: 10 }}>Tus declaraciones</h3>

          {declaraciones.length === 0 && (
            <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '30px 0' }}>
              Aún no tienes ninguna declaración. Crea la primera arriba.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {declaraciones.map(decl => (
              <div key={decl.id} style={{
                background: '#fff', border: `1px solid ${AZUL}1a`, borderRadius: 12,
                padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
                borderLeft: `4px solid ${decl.estado === 'finalizada' ? DORADO : AZUL_CLARO}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 700, color: AZUL, fontSize: 15 }}>
                    Declaración {decl.anio_gravable}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 999,
                    background: decl.estado === 'finalizada' ? `${DORADO}22` : `${AZUL_CLARO}22`,
                    color: decl.estado === 'finalizada' ? '#8a6d1a' : AZUL,
                    textTransform: 'uppercase',
                  }}>
                    {decl.estado === 'finalizada' ? 'Finalizada' : 'Borrador'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  Actualizada: {new Date(decl.updated_at).toLocaleDateString('es-CO')}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => abrirDeclaracion(decl)}
                    style={{
                      padding: '8px 12px', borderRadius: 8, border: 'none',
                      background: AZUL, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {decl.estado === 'finalizada' ? 'Ver / Editar' : 'Continuar'}
                  </button>
                  {decl.estado === 'finalizada' && (
                    <button
                      onClick={() => generarYDescargarPDF(decl)}
                      disabled={generandoPDF}
                      style={{
                        padding: '8px 12px', borderRadius: 8, border: `1px solid ${DORADO}`,
                        background: 'transparent', color: '#8a6d1a', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {generandoPDF ? 'Generando...' : 'Descargar PDF'}
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmEliminar(decl.id)}
                    style={{
                      padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca',
                      background: 'transparent', color: '#b91c1c', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      marginLeft: 'auto',
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>

        {confirmEliminar && (
          <ModalConfirmacion
            titulo="¿Eliminar declaración?"
            texto="Esta acción no se puede deshacer."
            textoBoton="Eliminar"
            colorBoton="#b91c1c"
            onConfirmar={() => eliminarDeclaracion(confirmEliminar)}
            onCancelar={() => setConfirmEliminar(null)}
          />
        )}

        <PdfOculto pdfRef={pdfRef} declaracion={declaracionPDF} nombreCompleto={nombreCompleto} />
      </div>
    )
  }

  const totalPreguntas = preguntasActuales.length
  const enResumen = pasoActual >= totalPreguntas

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb' }}>
      <header style={{
        background: AZUL, padding: '18px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#fff' }}>
          🧾 Declaración {declaracionActual?.anio_gravable}
        </h2>
        <button
          onClick={retroceder}
          style={{
            padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)',
            background: 'transparent', fontSize: 12, color: '#fff', cursor: 'pointer',
          }}
        >
          {pasoActual === 0 ? 'Salir' : '← Atrás'}
        </button>
      </header>

      {!enResumen && (
        <div style={{ height: 6, background: '#e2e8f0' }}>
          <div style={{
            height: '100%', width: `${((pasoActual) / Math.max(totalPreguntas, 1)) * 100}%`,
            background: `linear-gradient(90deg, ${AZUL}, ${DORADO})`, transition: 'width 0.3s',
          }} />
        </div>
      )}

      <main style={{ maxWidth: 560, margin: '0 auto', padding: 24 }}>
        {error && (
          <div style={{
            fontSize: 13, color: '#b91c1c', background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {!enResumen && preguntaActual && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 20px rgba(22,48,92,0.08)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: DORADO, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              {preguntaActual.seccion} · Pregunta {pasoActual + 1} de {totalPreguntas}
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 18 }}>
              <h3 style={{ fontSize: 19, fontWeight: 700, color: AZUL, margin: 0, flex: 1 }}>
                {preguntaActual.pregunta}
              </h3>
              <button
                onClick={() => setMostrarAyuda(p => !p)}
                title="¿Qué significa esto?"
                style={{
                  flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                  border: `1.5px solid ${AZUL_CLARO}`, background: mostrarAyuda ? AZUL_CLARO : '#fff',
                  color: mostrarAyuda ? '#fff' : AZUL_CLARO, fontWeight: 700, fontSize: 13,
                  cursor: 'pointer', lineHeight: '22px',
                }}
              >
                ?
              </button>
            </div>

            {mostrarAyuda && (
              <div style={{
                background: '#eef3fb', borderLeft: `3px solid ${AZUL_CLARO}`, borderRadius: 8,
                padding: '10px 14px', marginBottom: 18,
              }}>
                <div style={{ fontSize: 13, color: AZUL, lineHeight: 1.5 }}>
                  {preguntaActual.ayuda}
                </div>

                {/* ── Asistente de IA: solo esta duda, nada más ── */}
                {!iaRespuesta && !iaCargando && (
                  <div style={{ marginTop: 12 }}>
                    {!iaAbierta ? (
                      <button
                        onClick={() => setIaAbierta(true)}
                        style={{
                          fontSize: 12, fontWeight: 700, color: AZUL_CLARO,
                          background: '#fff', border: `1px solid ${AZUL_CLARO}55`,
                          borderRadius: 999, padding: '6px 12px', cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        🤖 ¿Aún tienes dudas? Pregúntale a la IA
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                        <textarea
                          value={iaDuda}
                          onChange={(e) => setIaDuda(e.target.value)}
                          placeholder="Escribe tu duda puntual sobre esta pregunta..."
                          rows={2}
                          maxLength={300}
                          style={{
                            ...inputStyle, fontSize: 13, resize: 'none', fontFamily: 'inherit',
                          }}
                        />
                        <button
                          onClick={preguntarIA}
                          disabled={!iaDuda.trim()}
                          style={{
                            alignSelf: 'flex-end', padding: '8px 14px', borderRadius: 8, border: 'none',
                            background: AZUL_CLARO, color: '#fff', fontWeight: 700, fontSize: 12,
                            cursor: 'pointer', opacity: iaDuda.trim() ? 1 : 0.5,
                          }}
                        >
                          Enviar
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {iaCargando && (
                  <div style={{ marginTop: 12, fontSize: 12, color: AZUL_CLARO, fontStyle: 'italic' }}>
                    🤖 Pensando...
                  </div>
                )}

                {iaError && (
                  <div style={{ marginTop: 12, fontSize: 12, color: '#b91c1c' }}>
                    {iaError}
                  </div>
                )}

                {iaRespuesta && (
                  <div style={{
                    marginTop: 12, background: '#fff', border: `1px solid ${AZUL_CLARO}33`,
                    borderRadius: 8, padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: AZUL_CLARO, marginBottom: 4 }}>
                      🤖 RESPUESTA
                    </div>
                    <div style={{ fontSize: 13, color: AZUL, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {iaRespuesta}
                    </div>
                    <button
                      onClick={nuevaDudaIA}
                      style={{
                        marginTop: 8, fontSize: 11, fontWeight: 700, color: AZUL_CLARO,
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      Hacer otra pregunta
                    </button>
                  </div>
                )}
              </div>
            )}

            {(preguntaActual.tipo === 'texto') && (
              <input
                type="text"
                autoFocus
                value={valorInput}
                onChange={(e) => setValorInput(e.target.value)}
                onKeyDown={manejarEnter}
                style={inputStyle}
              />
            )}

            {preguntaActual.tipo === 'numero' && (
              <input
                type="number"
                inputMode="numeric"
                min="0"
                autoFocus
                placeholder="0"
                value={valorInput}
                onChange={(e) => setValorInput(e.target.value)}
                onKeyDown={manejarEnter}
                style={inputStyle}
              />
            )}

            {preguntaActual.tipo === 'moneda' && (
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: 13, color: '#94a3b8', fontSize: 16 }}>$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  placeholder="0"
                  value={formatearMiles(valorInput)}
                  onChange={(e) => {
                    const soloDigitos = e.target.value.replace(/\D/g, '')
                    setValorInput(soloDigitos)
                  }}
                  onKeyDown={manejarEnter}
                  style={{ ...inputStyle, paddingLeft: 26 }}
                />
              </div>
            )}

            {preguntaActual.tipo === 'booleano' && (
              <div style={{ display: 'flex', gap: 10 }}>
                {[{ v: true, label: 'Sí' }, { v: false, label: 'No' }].map(op => (
                  <button
                    key={String(op.v)}
                    onClick={() => setValorInput(op.v)}
                    style={{
                      flex: 1, padding: '14px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${valorInput === op.v ? AZUL : '#e2e8f0'}`,
                      background: valorInput === op.v ? AZUL : '#fff',
                      color: valorInput === op.v ? '#fff' : AZUL,
                      fontWeight: 700, fontSize: 15,
                    }}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            )}

            {/* ── Advertencia especial: Honorarios con 2+ empleados ── */}
            {preguntaActual.id === 'honorarios_empleados' && valorInput === true && (
              <div style={{
                marginTop: 14, background: '#fff8e1', border: `1px solid ${DORADO}66`,
                borderRadius: 10, padding: 14,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#8a6d1a', marginBottom: 6 }}>
                  ⚠️ Esto cambia tu cédula
                </div>
                <p style={{ fontSize: 12.5, color: '#6b5814', lineHeight: 1.6, margin: '0 0 10px' }}>
                  Cuando vinculas 2 o más trabajadores o contratistas por al menos 90 días durante el año,
                  la DIAN ya no te considera un prestador de servicios independiente "puro": tu actividad se
                  parece más a un negocio, con costos operativos más amplios (nómina, arriendo del local,
                  insumos, etc.). Por eso el Formulario 210 pide declarar estos ingresos en la cédula de{' '}
                  <strong>Rentas No Laborales</strong> en lugar de Honorarios — ahí puedes descontar ese tipo
                  de costos, cosa que en Honorarios no aplicaría en tu caso.
                </p>
                <p style={{ fontSize: 12.5, color: '#6b5814', lineHeight: 1.6, margin: '0 0 12px' }}>
                  Te sugerimos mover automáticamente esta cédula a Rentas No Laborales para que registres
                  ahí tus ingresos, con las preguntas correctas para tu caso.
                </p>
                <button
                  onClick={moverAHonorariosNoLaboral}
                  disabled={guardando}
                  style={{
                    width: '100%', padding: '10px', borderRadius: 8, border: 'none',
                    background: DORADO, color: '#fff', fontWeight: 700, fontSize: 13,
                    cursor: 'pointer', opacity: guardando ? 0.7 : 1,
                  }}
                >
                  {guardando ? 'Actualizando...' : 'Cambiar a Rentas No Laborales'}
                </button>
              </div>
            )}

            {preguntaActual.tipo === 'multiselect_cedulas' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {CEDULAS_F210_INFO.map(cedula => {
                  const marcada = valorInput.includes(cedula.id)
                  return (
                    <button
                      key={cedula.id}
                      disabled={!cedula.disponible}
                      onClick={() => {
                        setValorInput(prev =>
                          marcada ? prev.filter(c => c !== cedula.id) : [...prev, cedula.id]
                        )
                      }}
                      style={{
                        textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: cedula.disponible ? 'pointer' : 'not-allowed',
                        border: `2px solid ${marcada ? AZUL : '#e2e8f0'}`,
                        background: marcada ? '#eef3fb' : '#fff',
                        opacity: cedula.disponible ? 1 : 0.55,
                        display: 'flex', flexDirection: 'column', gap: 2,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: AZUL, fontSize: 14 }}>
                          {marcada ? '☑' : '☐'} {cedula.label}
                        </span>
                        {!cedula.disponible && (
                          <span style={{
                            fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                            background: `${DORADO}22`, color: '#8a6d1a', textTransform: 'uppercase',
                          }}>
                            Próximamente
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: 12, color: '#64748b' }}>{cedula.descripcion}</span>
                    </button>
                  )
                })}
              </div>
            )}

            <button
              onClick={guardarPasoActual}
              disabled={guardando}
              style={{
                marginTop: 22, width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                background: `linear-gradient(135deg, ${AZUL}, ${AZUL_CLARO})`,
                color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                opacity: guardando ? 0.7 : 1,
              }}
            >
              {guardando ? 'Guardando...' : 'Guardar y continuar →'}
            </button>
          </div>
        )}

        {enResumen && declaracionActual && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 20px rgba(22,48,92,0.08)' }}>
            <h3 style={{ color: AZUL, marginTop: 0 }}>Resumen de tu declaración</h3>
            <ResumenRespuestas declaracion={declaracionActual} />

            {!resultadoImpuesto ? (
              <button
                onClick={calcularResultado}
                style={{
                  marginTop: 20, width: '100%', padding: '14px', borderRadius: 10, border: `2px solid ${AZUL}`,
                  background: '#fff', color: AZUL, fontWeight: 700, fontSize: 15, cursor: 'pointer',
                }}
              >
                🧮 Calcular impuesto de renta
              </button>
            ) : (
              <div style={{
                marginTop: 20, background: '#eef3fb', border: `1px solid ${AZUL}33`,
                borderRadius: 12, padding: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: AZUL, textTransform: 'uppercase', marginBottom: 10 }}>
                  Resultado del cálculo
                </div>
                {[
                  ['Renta líquida cédula general', resultadoImpuesto.rentaLiquidaCedulaGeneral],
                  ['Renta líquida pensiones', resultadoImpuesto.rentaLiquidaPensiones],
                  ['Base gravable total', resultadoImpuesto.baseGravableTotal],
                  ['Impuesto calculado', resultadoImpuesto.impuestoCalculado],
                  ['Total retenciones', resultadoImpuesto.totalRetenciones],
                ].map(([label, valor]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
                    <span style={{ color: '#64748b' }}>{label}</span>
                    <span style={{ fontWeight: 700, color: AZUL }}>{formatoMoneda(valor)}</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', fontSize: 15, marginTop: 8,
                  paddingTop: 8, borderTop: `2px solid ${AZUL}`,
                }}>
                  <span style={{ fontWeight: 800, color: AZUL }}>
                    {resultadoImpuesto.esAFavor ? 'Saldo a favor' : 'Saldo a pagar'}
                  </span>
                  <span style={{ fontWeight: 800, color: resultadoImpuesto.esAFavor ? '#16a34a' : AZUL }}>
                    {formatoMoneda(Math.abs(resultadoImpuesto.saldoAPagarOFavor))}
                  </span>
                </div>

                {!explicacionIA && !cargandoExplicacion && (
                  <button
                    onClick={explicarResultadoConIA}
                    style={{
                      marginTop: 12, width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${AZUL_CLARO}55`,
                      background: '#fff', color: AZUL_CLARO, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                    }}
                  >
                    🤖 Explicar este resultado
                  </button>
                )}
                {cargandoExplicacion && (
                  <div style={{ marginTop: 12, fontSize: 12, color: AZUL_CLARO, fontStyle: 'italic' }}>
                    🤖 Analizando tu resultado...
                  </div>
                )}
                {explicacionIA && (
                  <div style={{ marginTop: 12, background: '#fff', borderRadius: 8, padding: 12, fontSize: 13, color: AZUL, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {explicacionIA}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setMostrarConfirmFinalizar(true)}
              disabled={generandoPDF}
              style={{
                marginTop: 20, width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                background: `linear-gradient(135deg, ${DORADO}, ${DORADO_CLARO})`,
                color: AZUL, fontWeight: 800, fontSize: 15, cursor: 'pointer',
                opacity: generandoPDF ? 0.7 : 1,
              }}
            >
              {generandoPDF ? 'Generando PDF...' : 'Finalizar y descargar PDF'}
            </button>
            <button
              onClick={() => setVista('lista')}
              style={{
                marginTop: 10, width: '100%', padding: '12px', borderRadius: 10,
                border: `1px solid ${AZUL}33`, background: 'transparent', color: AZUL,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Volver a la lista (guardado como borrador)
            </button>
          </div>
        )}
      </main>

      {mostrarConfirmFinalizar && (
        <ModalConfirmacion
          titulo="¿Finalizar declaración?"
          texto="Se marcará como finalizada y se generará el PDF. Podrás seguir editándola después si lo necesitas."
          textoBoton="Sí, finalizar"
          colorBoton={AZUL}
          onConfirmar={finalizarYDescargar}
          onCancelar={() => setMostrarConfirmFinalizar(false)}
        />
      )}

      <PdfOculto pdfRef={pdfRef} declaracion={declaracionPDF} nombreCompleto={nombreCompleto} />
    </div>
  )
}

function ModalConfirmacion({ titulo, texto, textoBoton, colorBoton, onConfirmar, onCancelar }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(22,48,92,0.5)', display: 'flex', justifyContent: 'center',
      alignItems: 'center', zIndex: 200, padding: 16, boxSizing: 'border-box',
    }}>
      <div style={{
        background: '#fff', padding: 24, borderRadius: 16, width: '100%', maxWidth: 320,
        display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'center',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: AZUL }}>{titulo}</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{texto}</div>
        <button
          onClick={onConfirmar}
          style={{
            padding: '12px', borderRadius: 10, background: colorBoton, color: '#fff',
            border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
          }}
        >
          {textoBoton}
        </button>
        <button
          onClick={onCancelar}
          style={{
            padding: '12px', borderRadius: 10, background: '#f1f5f9', color: '#334155',
            border: 'none', cursor: 'pointer', fontWeight: 600,
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}

function ResumenRespuestas({ declaracion }) {
  const preguntas = construirPreguntas(declaracion)
  const porSeccion = {}
  preguntas.forEach(p => {
    if (!porSeccion[p.seccion]) porSeccion[p.seccion] = []
    porSeccion[p.seccion].push(p)
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {Object.entries(porSeccion).map(([seccion, preguntasSeccion]) => (
        <div key={seccion}>
          <div style={{ fontSize: 12, fontWeight: 800, color: DORADO, textTransform: 'uppercase', marginBottom: 6 }}>
            {seccion}
          </div>
          {preguntasSeccion.map(p => {
            const valor = obtenerValorRespuesta(declaracion, p.id)
            let valorMostrado = valor
            if (p.tipo === 'moneda') valorMostrado = formatoMoneda(valor)
            if (p.tipo === 'booleano') valorMostrado = valor ? 'Sí' : 'No'
            if (p.tipo === 'multiselect_cedulas') {
              valorMostrado = (valor || []).map(id => obtenerInfoCedulaF210(id)?.label || id).join(', ')
            }
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>{p.pregunta}</span>
                <span style={{ fontWeight: 700, color: AZUL }}>{valorMostrado}</span>
              </div>
            )
          })}
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, paddingTop: 8, borderTop: `2px solid ${AZUL}` }}>
        <span style={{ fontWeight: 700, color: AZUL }}>Patrimonio líquido</span>
        <span style={{ fontWeight: 800, color: AZUL }}>
          {formatoMoneda((declaracion.patrimonio_bruto || 0) - (declaracion.deudas || 0))}
        </span>
      </div>
    </div>
  )
}

function PdfOculto({ pdfRef, declaracion, nombreCompleto }) {
  return (
    <div style={{ position: 'fixed', top: 0, left: -99999, width: 700 }}>
      <div
        ref={pdfRef}
        style={{
          position: 'relative', background: '#ffffff', color: AZUL,
          padding: 40, fontFamily: 'Arial, sans-serif', minHeight: 400,
          overflow: 'hidden',
        }}
      >
        <img
          src={marcaAguaLogo}
          alt=""
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', width: 340, opacity: 0.06, pointerEvents: 'none',
          }}
        />
        {declaracion && (
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ borderBottom: `3px solid ${DORADO}`, paddingBottom: 14, marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: DORADO, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Formulario 210 · Guía de apoyo · Colombia
              </div>
              <h1 style={{ fontSize: 24, color: AZUL, margin: '4px 0 0' }}>
                Declaración de Renta {declaracion.anio_gravable}
              </h1>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{nombreCompleto}</div>
            </div>

            <ResumenRespuestas declaracion={declaracion} />

            <div style={{
              marginTop: 30, paddingTop: 14, borderTop: '1px solid #e2e8f0',
              fontSize: 10, color: '#94a3b8', lineHeight: 1.6,
            }}>
              Este documento es una guía de apoyo generada por Calypso para el diligenciamiento del Formulario 210 ante la DIAN.
              Aplica únicamente para declarantes en Colombia. Verifica la información con tu contador antes de presentar tu declaración oficial.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}